export type LoginResponse = {
  accessToken: string;
  refreshToken: string;
  deviceId: string;
  // Cabang aktif sesi (RFC-007). Server otoritatif: branchId diambil dari
  // token, bukan body. branchViewAll = boleh lintas cabang (Pemilik).
  branchId: string;
  branchViewAll: boolean;
  expiresIn: number;
  account: { id: string; name: string; role: string; permissions: string[] };
};

// Pengiriman opsional pada sale (US-SALE-11). Ongkir manual; paidBy
// "pelanggan" = masuk tagihan, "toko" = gratis ongkir (potong laba toko).
export type DeliveryInput = {
  ongkir: number;
  paidBy: "pelanggan" | "toko";
  address?: string;
  status?: string;
};

export type SaleRequest = {
  id: string;
  receiptNo?: string; // nomor struk (server prefix cabang bila perlu); tanpa ini server pakai UUID
  cashierId: string;
  shiftId?: string;
  customerId?: string;
  channelId?: string;
  discountTotal: number;
  // Poin loyalty yang ditukar jadi kredit pembayaran (opsional). Server
  // memvalidasi saldo & kurs; dipotong setelah transaksi sukses.
  loyaltyRedeem?: number;
  items: Array<{ variantId: string; qty: number; unitPrice: number }>;
  // Rincian pembayaran (tunai/QRIS/transfer + split). Forward-compat:
  // backend v1 belum mempersist ini (BodyParser abaikan field tak dikenal).
  payments?: Array<{ method: string; amount: number }>;
  delivery?: DeliveryInput;
};

export type ProductExtras = {
  description?: string;
  brand?: string;
  weightGram?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
};

export type BootstrapProduct = {
  id: string;
  name: string;
  category: string;
  subcategory?: string;
  imageUrl?: string;
  description?: string;
  brand?: string;
  weightGram?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  draft?: boolean; // produk hasil scan yang belum dilengkapi (disembunyikan dari kasir)
  variants: Array<{ id: string; sku: string; barcode: string; label: string; stock: number; price: number; floor: number }>;
};

// Respons buat/scan draft (bentuk CatalogProduct: kategori = `cat`).
export type DraftProduct = ProductExtras & {
  id: string;
  name: string;
  cat: string;
  subcategory?: string;
  imageUrl?: string;
  draft?: boolean;
  variants: Array<{ id: string; sku: string; barcode: string; label: string; stock: number; price: number }>;
};

// createDraftProduct: simpan barcode hasil scan sebagai draft (atau kembalikan
// produk yang sudah ada bila barcode terdaftar). US: scan di Add Product.
export function createDraftProduct(token: string, barcode: string) {
  return request<DraftProduct>("/products/draft", { method: "POST", body: JSON.stringify({ barcode }) }, token);
}

const API_BASE = import.meta.env.VITE_POS_API_URL || "/api";

// Pesan dari middleware JWT saat access token kedaluwarsa/invalid. Hanya ini
// yang memicu refresh/logout; 401 bisnis lain ("PIN lama salah", "Akun atau
// PIN tidak valid") TIDAK boleh mengeluarkan pengguna.
const TOKEN_EXPIRED = /Token tidak valid|Token wajib diisi/;

// Jembatan ke AuthProvider: kembalikan access token baru bila refresh sukses,
// atau null bila gagal. Diisi lewat setRefreshHandler saat app mount.
type RefreshHandler = () => Promise<string | null>;
let refreshHandler: RefreshHandler | null = null;
let inflightRefresh: Promise<string | null> | null = null;

export function setRefreshHandler(handler: RefreshHandler | null): void {
  refreshHandler = handler;
}

// Single-flight: banyak permintaan yang 401 berbarengan hanya memicu SATU
// panggilan refresh; semuanya menunggu hasil yang sama.
function refreshOnce(): Promise<string | null> {
  if (!refreshHandler) return Promise.resolve(null);
  if (!inflightRefresh) {
    inflightRefresh = refreshHandler().finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
}

// errorMessage: ambil pesan dari body error backend. Format normal:
// {"error": "..."} → kembalikan isinya; selain itu pakai teks apa adanya.
function errorMessage(body: string): string {
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed.error === "string") return parsed.error;
  } catch {
    // bukan JSON — pakai teks mentah.
  }
  return body;
}

async function request<T>(path: string, init: RequestInit = {}, token = "", retry = true): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!response.ok) {
    const body = await response.text();
    if (response.status === 401 && TOKEN_EXPIRED.test(body)) {
      // Coba perpanjang sesi dgn refresh token, lalu ulang permintaan SEKALI.
      if (retry && token) {
        const fresh = await refreshOnce();
        if (fresh) return request<T>(path, init, fresh, false);
      }
      // Refresh gagal / tak ada → keluarkan sesi & arahkan ke login.
      if (typeof window !== "undefined") window.dispatchEvent(new Event("pos:unauthorized"));
    }
    // Backend membungkus error sebagai {"error": "..."} — ambil pesannya agar
    // toast menampilkan alasan jelas (mis. "shift sudah ditutup"), bukan JSON.
    throw new Error(errorMessage(body) || `HTTP ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export type RefreshResponse = {
  accessToken: string;
  refreshToken: string;
  deviceId: string;
  // Refresh mempertahankan cabang aktif dari token lama; FE tak perlu
  // mengubah konteks cabang saat rotasi token rutin.
  expiresIn: number;
};

// ---- Multi-cabang (RFC-007) ----
export type Branch = {
  id: string;
  code: string;
  name: string;
  address?: string;
  timezone?: string;
  active?: boolean;
};

export type MyBranch = { branchId: string; isDefault: boolean };

// Daftar semua cabang (butuh autentikasi). Dipakai pemilih cabang & resolusi
// kode cabang untuk prefix struk.
export function listBranches(token: string) {
  return request<{ branches: Branch[] }>("/branches", {}, token);
}

// Cabang yang ditugaskan ke akun aktif (UserBranch). Dipakai pemilih cabang
// bagi user tanpa branchViewAll.
export function myBranches(token: string) {
  return request<{ branches: MyBranch[] }>("/branches/mine", {}, token);
}

export type BranchInput = { id?: string; code: string; name: string; address?: string; timezone?: string };
export function createBranch(token: string, b: BranchInput) {
  return request<Branch>("/branches", { method: "POST", body: JSON.stringify(b) }, token);
}
export function updateBranch(token: string, id: string, b: { code: string; name: string; address?: string; timezone?: string; active: boolean }) {
  return request<Branch>(`/branches/${id}`, { method: "PUT", body: JSON.stringify(b) }, token);
}
export function assignUserToBranch(token: string, branchId: string, payload: { userId: string; isDefault: boolean }) {
  return request<{ status: string }>(`/branches/${branchId}/assign`, { method: "POST", body: JSON.stringify(payload) }, token);
}

// Harga override untuk cabang AKTIF sesi (server ambil branchId dari token).
export function setBranchPrice(token: string, variantId: string, price: number) {
  return request<{ branchId: string; variantId: string; price: number }>(
    `/variants/${variantId}/branch-price`,
    { method: "PUT", body: JSON.stringify({ price }) },
    token,
  );
}

// Transfer stok dari cabang AKTIF sesi ke cabang tujuan (modal = COGS asal).
export function transferStock(token: string, payload: { toBranch: string; variantId: string; qty: number }) {
  return request<{ id: string; fromBranch: string; toBranch: string; variantId: string; qty: number; unitCost: number }>(
    "/stock/transfer",
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export type SwitchBranchResponse = {
  accessToken: string;
  refreshToken: string;
  deviceId: string;
  branchId: string;
  branchViewAll: boolean;
  expiresIn: number;
};

// Ganti cabang aktif device/sesi (RFC-007 §5). branchId DI SINI memang dari
// body — ini aksi sadar memilih cabang, bukan atribusi transaksi. Server
// re-otorisasi (user harus berhak atas cabang / punya branchViewAll) lalu
// menerbitkan ulang token dengan cabang baru.
export function switchBranch(token: string, branchId: string) {
  return request<SwitchBranchResponse>("/auth/switch-branch", {
    method: "POST",
    body: JSON.stringify({ branchId }),
  }, token);
}

// Rotasi token: kirim refresh token, dapat access+refresh baru. retry=false
// agar tak memicu loop refresh bila refresh token sendiri sudah invalid.
export function refreshSession(refreshToken: string) {
  return request<RefreshResponse>(
    "/auth/refresh",
    { method: "POST", body: JSON.stringify({ refreshToken }) },
    "",
    false,
  );
}

export function login(accountId: string, pin: string) {
  return request<LoginResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ accountId, pin }),
  });
}

export function changePin(token: string, oldPin: string, newPin: string) {
  return request<{ status: string }>("/auth/change-pin", {
    method: "POST",
    body: JSON.stringify({ oldPin, newPin }),
  }, token);
}

// Verifikasi PIN akun sendiri (buka area sensitif spt Kunci cipher). Tak
// menerbitkan token; melempar Error bila PIN salah (401).
export function verifyPin(token: string, pin: string) {
  return request<{ status: string }>("/auth/verify-pin", {
    method: "POST",
    body: JSON.stringify({ pin }),
  }, token);
}

export function createSale(token: string, sale: SaleRequest) {
  return request<{ id: string; status: string }>("/sales", {
    method: "POST",
    body: JSON.stringify(sale),
  }, token);
}

export function voidSale(token: string, id: string) {
  return request<{ status: string }>(`/sales/${id}/void`, { method: "POST" }, token);
}

export function returnSale(token: string, id: string) {
  return request<{ status: string }>(`/sales/${id}/return`, { method: "POST" }, token);
}

export function exchangeSale(token: string, id: string, sale: SaleRequest) {
  return request<{ status: string; newSaleId: string; additionalPayment: number; forfeited: number }>(`/sales/${id}/exchange`, {
    method: "POST",
    body: JSON.stringify(sale),
  }, token);
}

export function syncSales(token: string, sales: SaleRequest[]) {
  return request<{ results: Array<{ id: string; status: string; error?: string }> }>("/sync/sales", {
    method: "POST",
    body: JSON.stringify({ sales }),
  }, token);
}

export type BootstrapResult = {
  products: BootstrapProduct[];
  // ID produk yang dihapus sejak cursor (hanya mode delta) → klien buang dari cache.
  deletedIds?: string[];
  settings?: Record<string, string>;
  // Kursor untuk pull delta berikutnya (RFC3339Nano). Klien menyimpannya &
  // mengirim balik via ?since agar bootstrap berikut hanya menarik perubahan.
  serverTime?: string;
};

// bootstrap: tanpa `since` → snapshot penuh; dengan `since` → hanya
// produk/varian yang berubah sejak cursor (US-OFFLINE-3 pull delta).
export function bootstrap(token: string, since?: string) {
  const query = since ? `?since=${encodeURIComponent(since)}` : "";
  return request<BootstrapResult>(`/sync/bootstrap${query}`, {}, token);
}

// Format SKU otomatis (dinamis, disimpan di settings).
export type SkuFormat = { prefix: string; nameLen: number; labelLen: number; separator: string };
export function setSkuFormat(token: string, fmt: SkuFormat) {
  return request<{ status: string }>("/settings/sku-format", { method: "PUT", body: JSON.stringify(fmt) }, token);
}

// Format struk: footer + awalan nomor struk (dinamis, di settings).
export type ReceiptFormat = { footer: string; numberPrefix: string };
export function setReceiptFormat(token: string, fmt: ReceiptFormat) {
  return request<{ status: string }>("/settings/receipt-format", { method: "PUT", body: JSON.stringify(fmt) }, token);
}

// Metode costing (FIFO/Weighted Average) — diatur Pemilik (US-SET-1).
export function setCostingMethod(token: string, method: string) {
  return request<{ status: string; method: string }>(
    "/settings/costing-method",
    { method: "PUT", body: JSON.stringify({ value: method }) },
    token,
  );
}

export type SalesForecast = {
  windowDays: number;
  horizonDays: number;
  forecastRevenue: number;
  baselinePerDay: number;
  trendPerDay: number;
  confidence: number;
  lowData: boolean;
  seasonal: boolean;
  historyDays: number;
};

// Forecast omzet periode berikut + flag kepercayaan (US-REPORT-4).
export function getSalesForecast(token: string, window = 30, horizon = 7) {
  return request<SalesForecast>(`/reports/forecast?window=${window}&horizon=${horizon}`, {}, token);
}

// Saldo poin loyalty pelanggan (untuk UI redeem di kasir).
export function getCustomerLoyalty(token: string, id: string) {
  return request<{ customerId: string; points: number }>(`/customers/${id}/loyalty`, {}, token);
}

export type CSVImportSummary = { created?: number; updated?: number; failed?: number; errors?: string[] };

// Import CSV produk (body = teks CSV mentah). Server balas ringkasan.
export function importProductsCSV(token: string, csv: string) {
  return request<CSVImportSummary>("/products/import-csv", { method: "POST", body: csv }, token);
}

// Export CSV produk (teks). Untuk Kasir, server tak menyertakan kolom modal.
export async function exportProductsCSV(token: string): Promise<string> {
  const res = await fetch(`${API_BASE}/products/export-csv`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  return res.text();
}

export function setCipherKey(token: string, value: string, unit: "rupiah" | "ribuan" = "rupiah") {
  return request<{ status: string }>("/settings/cipher-key", {
    method: "PUT",
    body: JSON.stringify({ value, unit }),
  }, token);
}

export function setStoreName(token: string, value: string) {
  return request<{ status: string }>("/settings/store-name", {
    method: "PUT",
    body: JSON.stringify({ value }),
  }, token);
}

export type Shift = {
  ID?: string;
  id?: string;
  cashierId?: string;
  openedAt?: string;
  openingCash?: number;
  actualCash?: number;
  status?: string;
};

export function openShift(token: string, cashierId: string, openingCash: number) {
  return request<Shift>("/shifts", {
    method: "POST",
    body: JSON.stringify({ cashierId, openingCash }),
  }, token);
}

// Shift terbuka store-wide (dipakai bersama semua user). {shift:null} bila
// belum ada yang membuka.
export function getCurrentShift(token: string) {
  return request<{ shift: Shift | null }>("/shifts/current", {}, token);
}

export type ShiftClose = {
  id: string;
  cashierId: string;
  status: string;
  openingCash: number;
  cashSales: number;
  cashIn: number;
  cashOut: number;
  expectedCash: number;
  actualCash: number;
  difference: number;
};

export function closeShift(token: string, id: string, actualCash: number) {
  return request<ShiftClose>(`/shifts/${id}/close`, {
    method: "POST",
    body: JSON.stringify({ actualCash }),
  }, token);
}

export function createCashMovement(token: string, payload: { shiftId: string; type: "in" | "out"; category: string; amount: number; note?: string; actorId?: string }) {
  return request<{ id?: string; ID?: string }>("/cash/movements", {
    method: "POST",
    body: JSON.stringify(payload),
  }, token);
}

export type SalesSummary = {
  netSales: number;
  grossSales: number;
  discountTotal: number;
  transactionCount: number;
  itemCount: number;
};

export type ProfitSummary = SalesSummary & { cogs: number; grossProfit: number; deliveryCost: number; netProfit: number };
export type AnomalyReport = { items: Array<{ type: string; actorId?: string; entityId?: string; count?: number; amount?: number; severity: string; note?: string }> };

// Laporan per cabang (RFC-007 §8): branchId hanya dihormati server bila sesi
// punya branch.viewAll (Pemilik). Kosong = konsolidasi semua cabang; per cabang
// = filter satu cabang. Non-viewAll (Manajer) selalu dikunci ke cabang sesi —
// param diabaikan server. FE mengirim param hanya saat berhak (defensif).
function branchQuery(branchId?: string, sep: "?" | "&" = "?") {
  return branchId ? `${sep}branchId=${encodeURIComponent(branchId)}` : "";
}

export function getSalesReport(token: string, branchId?: string) {
  return request<SalesSummary>(`/reports/sales${branchQuery(branchId)}`, {}, token);
}

export type DailySalesPoint = { date: string; netSales: number; txCount: number };
export function getDailySales(token: string, days = 14, branchId?: string) {
  return request<{ items: DailySalesPoint[] }>(`/reports/sales/daily?days=${days}${branchQuery(branchId, "&")}`, {}, token);
}

export type DailyCashPoint = { date: string; cashSales: number; cashIn: number; cashOut: number; net: number };
export function getDailyCash(token: string, days = 14, branchId?: string) {
  return request<{ items: DailyCashPoint[] }>(`/reports/cash/daily?days=${days}${branchQuery(branchId, "&")}`, {}, token);
}

// Rincian kas masuk/keluar non-penjualan (untuk breakdown per hari di halaman
// Kas). Terbaru dulu; agregat tetap dari getDailyCash.
export type CashMovementItem = { id: string; type: "in" | "out"; category: string; amount: number; note: string; actorId: string; at: string };
export function getCashMovements(token: string, days = 14, branchId?: string) {
  return request<{ items: CashMovementItem[] }>(`/reports/cash/movements?days=${days}${branchQuery(branchId, "&")}`, {}, token);
}

// Rincian per-struk setoran tunai ke laci (tunai bersih = tender tunai −
// kembalian). Untuk daftar struk per hari di halaman Kas. Terbaru dulu.
export type CashReceiptItem = { receiptNo: string; total: number; cash: number; at: string };
export function getCashReceipts(token: string, days = 14, branchId?: string) {
  return request<{ items: CashReceiptItem[] }>(`/reports/cash/receipts?days=${days}${branchQuery(branchId, "&")}`, {}, token);
}

export function getProfitReport(token: string, branchId?: string) {
  return request<ProfitSummary>(`/reports/profit${branchQuery(branchId)}`, {}, token);
}

export function getAnomalyReport(token: string, branchId?: string) {
  return request<AnomalyReport>(`/reports/anomalies${branchQuery(branchId)}`, {}, token);
}

// ---- Riwayat transaksi (detail penjualan) ----
export type SaleListItem = { id: string; receiptNo: string; cashierId: string; status: string; discountTotal: number; total: number; itemCount: number; createdAt: string };
export type SaleDetailItem = { variantId: string; name: string; sku: string; label: string; qty: number; unitPrice: number; discountShare: number; netUnitPrice: number; lineTotal: number; returnedQty: number };
export type SalePayment = { method: string; amount: number; status: string };
export type SaleDelivery = { ongkir: number; paidBy: "pelanggan" | "toko"; address: string; status: string };
export type SaleDetail = { id: string; receiptNo: string; cashierId: string; channelId: string; shiftId: string; status: string; discountTotal: number; total: number; createdAt: string; items: SaleDetailItem[]; payments?: SalePayment[]; delivery?: SaleDelivery };

export function getSales(token: string, cursor = "") {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  return request<{ items: SaleListItem[]; nextCursor: string }>(`/sales${qs}`, {}, token);
}

export function getSale(token: string, id: string) {
  return request<SaleDetail>(`/sales/${id}`, {}, token);
}

// ---- Manajemen produk & stok (fase 3) ----
export type ProductVariantInput = { id: string; sku: string; barcode: string; label: string; price: number };
export type CreateProductInput = ProductExtras & { id: string; name: string; cat: string; subcategory?: string; art?: string; imageUrl?: string; variants: ProductVariantInput[] };

export function createProduct(token: string, product: CreateProductInput) {
  return request<BootstrapProduct>("/products", { method: "POST", body: JSON.stringify(product) }, token);
}

export function updateProduct(token: string, id: string, p: ProductExtras & { name: string; cat: string; subcategory?: string; art?: string; imageUrl?: string; draft?: boolean }) {
  return request<BootstrapProduct>(`/products/${id}`, { method: "PUT", body: JSON.stringify(p) }, token);
}

export function deleteProduct(token: string, id: string) {
  return request<{ status: string }>(`/products/${id}`, { method: "DELETE" }, token);
}

export function addVariant(token: string, productId: string, v: ProductVariantInput) {
  return request<{ id: string; sku: string; label: string; price: number }>(`/products/${productId}/variants`, { method: "POST", body: JSON.stringify(v) }, token);
}

export function updateVariant(token: string, variantId: string, v: Omit<ProductVariantInput, "id">) {
  return request<{ id: string; sku: string; label: string; price: number }>(`/variants/${variantId}`, { method: "PUT", body: JSON.stringify(v) }, token);
}

export function deleteVariant(token: string, variantId: string) {
  return request<{ status: string }>(`/variants/${variantId}`, { method: "DELETE" }, token);
}

// Upload foto: multipart (FormData) — jangan set Content-Type manual, biar
// browser pasang boundary. Mengembalikan URL yang bisa dipakai di <img src>.
export async function uploadImage(token: string, file: File): Promise<{ url: string }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE}/uploads`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  return res.json() as Promise<{ url: string }>;
}

// Manajemen file S3/R2 (developer-only; permission storage.manage).
export type StorageObject = { key: string; size: number; lastModified: string; url: string };
export type StorageFolder = { name: string; prefix: string };
export function getStorageFiles(token: string, prefix = "") {
  const qs = prefix ? `?prefix=${encodeURIComponent(prefix)}` : "";
  return request<{ prefix: string; folders: StorageFolder[]; files: StorageObject[] }>(`/storage/files${qs}`, {}, token);
}
export function deleteStorageFiles(token: string, keys: string[]) {
  return request<{ deleted: number }>("/storage/files/delete", { method: "POST", body: JSON.stringify({ keys }) }, token);
}
export function createStorageFolder(token: string, path: string) {
  return request<{ prefix: string }>("/storage/folders", { method: "POST", body: JSON.stringify({ path }) }, token);
}
export function renameStorageFolder(token: string, from: string, to: string) {
  return request<{ moved: number; prefix: string }>("/storage/folders/rename", { method: "POST", body: JSON.stringify({ from, to }) }, token);
}
// Upload/timpa gambar ke prefix tertentu (multipart). Non-gambar ditolak server.
export async function uploadStorageFile(token: string, prefix: string, file: File): Promise<{ key: string; url: string }> {
  const form = new FormData();
  form.append("prefix", prefix);
  form.append("file", file);
  const res = await fetch(`${API_BASE}/storage/files/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  return res.json() as Promise<{ key: string; url: string }>;
}

// Template pesan WhatsApp (setting wa.templates). GET boleh semua user authed
// (kasir butuh template struk); PUT hanya settings.edit.
export function getWATemplates(token: string) {
  return request<{ templates: Record<string, string> }>("/settings/wa-templates", {}, token);
}
export function setWATemplates(token: string, templates: Record<string, string>) {
  return request<{ templates: Record<string, string> }>(
    "/settings/wa-templates",
    { method: "PUT", body: JSON.stringify({ templates }) },
    token,
  );
}

export type StockInInput = { id: string; supplierId?: string; lotNo: string; qty: number; landedCost: number; receivedAt?: string };
export function stockIn(token: string, variantId: string, lot: StockInInput) {
  return request<{ status: string }>(`/variants/${variantId}/stock-in`, { method: "POST", body: JSON.stringify(lot) }, token);
}

export type BlindCountResult = { id: string; variantId: string; countedQty: number; systemQty: number; diff: number; actorId: string; note: string; at: string };
export function blindCount(token: string, variantId: string, payload: { countedQty: number; note?: string }) {
  return request<BlindCountResult>(`/variants/${variantId}/blind-count`, { method: "POST", body: JSON.stringify(payload) }, token);
}

// ---- Master data: supplier, channel, pelanggan ----
export type Supplier = { id: string; name: string; waNumber?: string; contact?: string; note?: string; active?: boolean };
export function getSuppliers(token: string) { return request<Supplier[]>("/suppliers", {}, token); }
export function createSupplier(token: string, s: Supplier) { return request<Supplier>("/suppliers", { method: "POST", body: JSON.stringify(s) }, token); }
export function updateSupplier(token: string, id: string, payload: { name: string; waNumber?: string; contact?: string; note?: string; active: boolean }) {
  return request<Supplier>(`/suppliers/${id}`, { method: "PUT", body: JSON.stringify(payload) }, token);
}

export type Category = { id: string; name: string; parent: string; active?: boolean };
export function getCategories(token: string) { return request<Category[]>("/categories", {}, token); }
export function createCategory(token: string, c: { id: string; name: string; parent: string }) {
  return request<Category>("/categories", { method: "POST", body: JSON.stringify(c) }, token);
}
export function updateCategory(token: string, id: string, payload: { name: string; parent: string; active: boolean }) {
  return request<Category>(`/categories/${id}`, { method: "PUT", body: JSON.stringify(payload) }, token);
}

export type Channel = { id: string; name: string; online?: boolean; active?: boolean };
export function getChannels(token: string) { return request<Channel[]>("/channels", {}, token); }
export function createChannel(token: string, c: Channel) { return request<Channel>("/channels", { method: "POST", body: JSON.stringify(c) }, token); }
export function updateChannel(token: string, id: string, payload: { name: string; online?: boolean; active: boolean }) {
  return request<Channel>(`/channels/${id}`, { method: "PUT", body: JSON.stringify(payload) }, token);
}

export type CustomerAddress = { id: string; label: string; address: string; isDefault: boolean };
export type Customer = { id: string; code?: string; name: string; waNumber?: string; optOut?: boolean; active?: boolean; points?: number; addresses?: CustomerAddress[] };

export type LoyaltyConfig = { mode: "off" | "point" | "stamp"; earnAmount: number };
export function setLoyalty(token: string, config: LoyaltyConfig) {
  return request<LoyaltyConfig>("/settings/loyalty", { method: "PUT", body: JSON.stringify(config) }, token);
}
export function getCustomers(token: string) { return request<Customer[]>("/customers", {}, token); }
export function createCustomer(token: string, c: Customer) { return request<Customer>("/customers", { method: "POST", body: JSON.stringify(c) }, token); }
export function updateCustomer(token: string, id: string, payload: { name: string; waNumber?: string; optOut?: boolean; active: boolean; addresses?: CustomerAddress[] }) {
  return request<Customer>(`/customers/${id}`, { method: "PUT", body: JSON.stringify(payload) }, token);
}

// ---- Pengaturan lanjut: diskon, akun, peran, ambang anomali ----
export type DiscountRule = { id: string; name: string; type: string; value: number; maxPerRole: number; active: boolean };
export function getDiscounts(token: string) { return request<{ rules: DiscountRule[] }>("/settings/discounts", {}, token); }
export function createDiscount(token: string, rule: DiscountRule) { return request<DiscountRule>("/settings/discounts", { method: "POST", body: JSON.stringify(rule) }, token); }

export type AccountInput = { id: string; username: string; name: string; role: string; active: boolean; pin: string };
export function createAccount(token: string, a: AccountInput) { return request<{ id: string; username: string; name: string; role: string; active: boolean }>("/settings/accounts", { method: "POST", body: JSON.stringify(a) }, token); }
export type ManagedAccount = { id: string; username: string; name: string; role: string; active: boolean };
export function getAccounts(token: string) { return request<{ accounts: ManagedAccount[] }>("/settings/accounts", {}, token); }
export function updateAccount(token: string, id: string, payload: { name: string; role: string; active: boolean }) {
  return request<ManagedAccount>(`/settings/accounts/${id}`, { method: "PUT", body: JSON.stringify(payload) }, token);
}
export function setOverrides(token: string, id: string, payload: { granted: string[]; revoked: string[] }) {
  return request<{ status: string }>(`/settings/accounts/${id}/overrides`, { method: "PUT", body: JSON.stringify(payload) }, token);
}

export type RoleInput = { id: string; name: string; active: boolean; permissions: string[] };
export function createRole(token: string, r: RoleInput) { return request<RoleInput>("/settings/roles", { method: "POST", body: JSON.stringify(r) }, token); }

export type AnomalyThresholds = { returnVoidPerActor?: number; highDiscount?: number; largeCashOut?: number; largeStockDiff?: number };
export function setAnomalyThresholds(token: string, value: AnomalyThresholds) {
  return request<{ status: string }>("/settings/anomaly-thresholds", { method: "PUT", body: JSON.stringify({ value }) }, token);
}

// ---- Restock & perangkat ----
export type RestockItem = { variantId: string; name: string; stock: number; velocity: number; daysRemaining: number };
export type RestockSupplier = { supplierId: string; supplierName: string; items: RestockItem[] };
export function getRestockSummary(token: string) { return request<{ suppliers: RestockSupplier[] }>("/restock/summary", {}, token); }
export function getRestockChatLink(token: string, variantIds: string[]) {
  return request<{ link: string }>(`/restock/chat-link?variantIds=${encodeURIComponent(variantIds.join(","))}`, {}, token);
}

export type Device = { id: string; name: string; platform: string; registeredAt: string; lastSeenAt: string; revokedAt: string | null };
export function getDevices(token: string) { return request<{ items: Device[] }>("/devices", {}, token); }
export function revokeDevice(token: string, id: string) { return request<{ status: string }>(`/devices/${id}`, { method: "DELETE" }, token); }

// ---- Marketplace stock-sync (v-next) — pemetaan SKU & kredensial konektor ----
export type MarketplaceListing = { id: string; variantId: string; channel: string; externalListingId: string; externalSku: string; active: boolean };
export type MarketplaceCredential = { id: string; channel: string; shopId: string; active: boolean; hasToken: boolean; expiresAt?: string };

export function getMarketplaceConnectors(token: string) { return request<string[]>("/marketplace/connectors", {}, token); }
export function getMarketplaceListings(token: string) { return request<MarketplaceListing[]>("/marketplace/listings", {}, token); }
export function createMarketplaceListing(token: string, payload: { variantId: string; channel: string; externalListingId: string; externalSku: string; active?: boolean }) {
  return request<MarketplaceListing>("/marketplace/listings", { method: "POST", body: JSON.stringify(payload) }, token);
}
export function deleteMarketplaceListing(token: string, id: string) { return request<{ status: string }>(`/marketplace/listings/${id}`, { method: "DELETE" }, token); }
export function getMarketplaceCredentials(token: string) { return request<MarketplaceCredential[]>("/marketplace/credentials", {}, token); }
export function saveMarketplaceCredential(token: string, payload: { channel: string; shopId: string; accessToken: string; refreshToken: string; expiresAt?: string }) {
  return request<MarketplaceCredential>("/marketplace/credentials", { method: "POST", body: JSON.stringify(payload) }, token);
}
export function syncMarketplace(token: string) { return request<{ status: string }>("/marketplace/sync", { method: "POST" }, token); }
// URL otorisasi OAuth marketplace (mis. Shopee). Frontend mengarahkan browser
// ke url ini; setelah merchant menyetujui, backend menyimpan token & redirect balik.
export function getMarketplaceOAuthUrl(token: string, channel: string) {
  return request<{ url: string }>(`/marketplace/oauth/${encodeURIComponent(channel)}/url`, {}, token);
}
